import { useEffect, useRef, useState } from 'react';
import {
  listTwinAssetImports,
  MIDDLEWARE_REQUEST_TIMEOUT_MS,
  type TwinAssetImportSummary,
} from '@repo/front-end-services';

/**
 * The twin's latest register upload, for the Equipment step's summary and file card
 * (`equipment-page-landed`, R1, R4, R5).
 *
 * The record is the server's (`equipment-page-import-record`), so what landed survives a reload —
 * browser memory did not. The newest record is the summary's source only while it is `complete`
 * and not undone: a newer upload replaces it, an undo removes it, and a failed or still-writing
 * upload has no summary (the uploader's *Upload failed.* banner says what happened).
 *
 * Read on arriving at the step for a twin, and again whenever `refreshKey` changes (the page bumps
 * it when an upload lands). A failure — any throw, a 403 or a 404 from a middleware without the
 * routes — yields no record and is logged once per twin visit; it is never shown as an error,
 * because the summary is additive and the register beneath it is right either way.
 *
 * Bounds: the service's request timeout (`MIDDLEWARE_REQUEST_TIMEOUT_MS`, 30 s) on the socket, and
 * an `AbortController` holding the same 30 s on the wall clock (the repo's authoritative bound —
 * axios's timeout is inactivity only). One attempt, no retry. The list is the server's newest 20,
 * and only the first is read, so a long history cannot hide the latest.
 */
export interface EquipmentImportSummaryArgs {
  /** Step 2 is on screen and the register on it is this twin's. */
  active: boolean;
  twinId: number | null | undefined;
  /** The address names two different twins: never call, as `reloadRegister` refuses. */
  conflicted: boolean;
  /** Bumped when an upload lands: read again. */
  refreshKey: number;
  /**
   * Bumped after a decision about the SAME record — a *Keep* or a *Remove* from the summary's *Not in
   * this file* list (`equipment-page-reimport`, R7, R8): read again, but keep showing the answer in
   * hand until the new one lands. Clearing it, as an upload's read does, would unmount the summary
   * under the operator's focus and its own announcement for the length of the read. The newer read
   * still wins: a change of either key aborts the read in flight.
   */
  quietKey?: number;
  baseUrl: string;
  getToken: () => Promise<string>;
}

/**
 * Whether a record is one the summary may speak for (R1): complete, not undone, and carrying the
 * counts it would report — a record with none would read "0 assets are in", passing an unknown off
 * as zero.
 */
export const isLiveImport = (record: TwinAssetImportSummary | undefined | null): boolean =>
  Boolean(record && record.state === 'complete' && record.undoneAt === null && record.counts);

/**
 * The upload the summary speaks for: the newest record, while it is live. An undone or failed one
 * has no summary, and an older one never stands in for it.
 */
export const latestLiveImport = (
  list: readonly TwinAssetImportSummary[],
): TwinAssetImportSummary | null => {
  const latest = list[0];
  return latest && isLiveImport(latest) ? latest : null;
};

/** The states an upload can be undone from (`equipment-page-undo-import`, R1). */
const UNDOABLE_STATES: ReadonlySet<TwinAssetImportSummary['state']> = new Set<
  TwinAssetImportSummary['state']
>(['complete', 'failed', 'partly_undone']);

/** The upload *Undo This Import* takes back. */
export interface UndoableImport {
  record: TwinAssetImportSummary;
  /** Part of it is undone, so the buttons read *Undo Again* (R7). */
  partlyUndone: boolean;
}

/**
 * The upload *Undo This Import* offers (`equipment-page-undo-import`, R1, R2): the newest record,
 * while it is complete, failed (the importer commits in chunks, so a failed upload may have written
 * rows) or partly undone. One still writing, being undone or undone offers nothing — and an older
 * upload never stands in for it, as with the summary.
 */
export const undoableImport = (list: readonly TwinAssetImportSummary[]): UndoableImport | null => {
  const record = list[0];
  if (!record || !UNDOABLE_STATES.has(record.state)) return null;
  return { record, partlyUndone: record.state === 'partly_undone' };
};

/**
 * What the read has answered. `unread` while it is in flight or after it failed — the file card
 * then falls back to this session's own upload (R5); `read` once the server has said, when the
 * record (or its absence) is the truth.
 */
export type ImportSummaryStatus = 'unread' | 'read';

export interface EquipmentImportSummary {
  record: TwinAssetImportSummary | null;
  /** The upload *Undo This Import* offers, from the same read (`equipment-page-undo-import`, R3). */
  undoable: UndoableImport | null;
  status: ImportSummaryStatus;
  /**
   * Whether the read for THIS twin and refresh has finished — answered or failed
   * (`equipment-page-adoption`, R11). The step's landing waits for it, because an adoption import
   * lands elsewhere; `status` alone cannot say so, as a failed read and one in flight are both
   * `unread`.
   */
  settled: boolean;
}

const statusOf = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : null;
};

const UNREAD: Omit<EquipmentImportSummary, 'settled'> = {
  record: null,
  undoable: null,
  status: 'unread',
};

export const useEquipmentImportSummary = ({
  active,
  twinId,
  conflicted,
  refreshKey,
  quietKey = 0,
  baseUrl,
  getToken,
}: EquipmentImportSummaryArgs): EquipmentImportSummary => {
  const [summary, setSummary] = useState<Omit<EquipmentImportSummary, 'settled'>>(UNREAD);
  // Which read last finished, as `twinId|refreshKey`: compared at render, so a new read is unsettled
  // from the render that asks for it, before its effect has run.
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const readKey = `${twinId ?? ''}|${refreshKey}`;
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  /** The twin whose failed read has been logged this visit. */
  const warnedFor = useRef<number | null>(null);
  /** The `readKey` the answer in hand is for: a quiet re-read keeps it only for that same key. */
  const shownFor = useRef<string | null>(null);

  // A different twin is a different register: nothing of the last one's upload may show.
  useEffect(() => {
    setSummary(UNREAD);
    warnedFor.current = null;
    shownFor.current = null;
  }, [twinId]);

  useEffect(() => {
    if (!active || conflicted || twinId == null) return;
    const forTwin = twinId;
    const forKey = `${forTwin}|${refreshKey}`;
    // A new read (an upload landed, or the step opened) supersedes what the last one said: until it
    // answers, nothing older speaks for the twin (review: the previous file named after an upload).
    // A quiet re-read of the same twin and refresh keeps it (`quietKey`).
    if (shownFor.current !== forKey) setSummary(UNREAD);
    const controller = new AbortController();
    // Set only by the deadline, so a timed-out read is told apart from this hook's own aborts.
    let timedOut = false;
    const deadline = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, MIDDLEWARE_REQUEST_TIMEOUT_MS);
    void (async () => {
      try {
        const token = await getTokenRef.current();
        const list = await listTwinAssetImports(baseUrl, token, forTwin, controller.signal);
        if (controller.signal.aborted) return;
        setSettledKey(forKey);
        shownFor.current = forKey;
        setSummary({
          record: latestLiveImport(list),
          undoable: undoableImport(list),
          status: 'read',
        });
        // A recovered read re-arms the warning, so a later run of failures is logged again.
        warnedFor.current = null;
      } catch (error) {
        // Aborted by a twin change, an unmount or a newer read: not a failure, and not this read's to report.
        if (controller.signal.aborted && !timedOut) return;
        setSettledKey(forKey);
        shownFor.current = null;
        setSummary(UNREAD);
        if (warnedFor.current !== forTwin) {
          warnedFor.current = forTwin;
          // The status only — never the raw error, whose request config carries the bearer token.
          console.warn('[equipment] the import list could not be read; no summary', {
            twinId: forTwin,
            status: timedOut ? 'timeout' : statusOf(error),
          });
        }
      } finally {
        window.clearTimeout(deadline);
      }
    })();
    return () => {
      window.clearTimeout(deadline);
      controller.abort();
    };
  }, [active, conflicted, twinId, refreshKey, quietKey, baseUrl]);

  return { ...summary, settled: settledKey === readKey };
};
